import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import { useGetMyBettieAutoTopUp } from '@pocapan/shared/domains/bettie/ui/hooks/useGetMyBettieAutoTopup';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { useWebViewBackground } from '../hooks/useWebViewBackground';
import { formatDateTime } from '@pocapan/shared/utils/DateUtil';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';

const ACTIVE_STEP_GRADIENT =
  'linear-gradient(90deg, #9F9DF9 0%, #D671F6 27%, #FFE175 52%, #5CDBF6 78%, #94A5F9 100%)';
const MAX_STEP = 10;

const AutoTopupEventPage = () => {
  const { t } = useTranslation();
  useWebViewBackground({ color: '#FFFFFF' });
  const handleHistoryBack = useHandleHistoryBack();
  
  const [isBettieChargeBottomSheetOpen, setIsBettieChargeBottomSheetOpen] = useState(false);

  const { data: myBettieAutoTopup } = useGetMyBettieAutoTopUp();
  const currentStep = myBettieAutoTopup?.chargeCount ?? 0;

  return (
    <div className="min-h-screen bg-background-light pb-[calc(env(safe-area-inset-bottom)+10px)]">
      <div className="relative">
        <button
          type="button"
          onClick={handleHistoryBack}
          className="absolute top-[calc(env(safe-area-inset-top)+10px)] left-[16px]"
        >
          <Xmark size={24} className="text-pure-black" />
        </button>
        <img
          src="/assets/bettie/heart_event_image.png"
          alt="heart event"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative z-[10] mt-[-15px]">
        <div className="px-[28px] flex items-center justify-between">
          <p className="text-[15px] font-medium leading-[18px] text-main-light-2">
            {t('AutoTopupEvent.progress')}
          </p>
          <p className="text-[12px] font-medium leading-[14px] text-main-light-4">
            {t('AutoTopupEvent.maxCharge', { count: MAX_STEP })}
          </p>
        </div>
        <div className="px-[24px] mt-[12px]">
          <div className="rounded-[14px] border border-separator-light-2 py-[16px] pl-[16px] pr-[18px]">
            <ProgressBar currentStep={currentStep} />
            <div className="flex items-center justify-between mt-[13px]">
              <p className="text-[12px] font-medium leading-[14px] text-main-light-3">
                {myBettieAutoTopup?.registeredAt
                  ? t('AutoTopupEvent.firstRegisterDate', {
                      date: formatDateTime(myBettieAutoTopup?.registeredAt, 'yyyy.MM.dd')
                    })
                  : t('AutoTopupEvent.startAutoTopup')}
              </p>
              <p className="text-[12px] font-medium leading-[14px] text-main-light-3">
                {t('AutoTopupEvent.currentCount', {
                  currentCount: currentStep,
                  maxCount: MAX_STEP
                })}
              </p>
            </div>
          </div>
        </div>
        <div className="px-[28px] mt-[27px]">
          <ul className="text-[13px] leading-[16px] text-main-light-3 list-disc list-outside pl-[1rem] flex flex-col items-start gap-[5px]">
            <li>{t('AutoTopupEvent.autoTopupDescription1')}</li>
            <li className="whitespace-pre-line">{t('AutoTopupEvent.autoTopupDescription2')}</li>
          </ul>
        </div>
        <div className="px-[20px]">
          <Button
            size="large"
            className="w-full text-[17px] font-semibold mt-[35px] rounded-[18px]"
            onClick={() => setIsBettieChargeBottomSheetOpen(true)}
          >
            {myBettieAutoTopup?.autoTopupId
              ? t('AutoTopupEvent.changeAutoTopup')
              : t('AutoTopupEvent.settingAutoTopup')}
          </Button>
        </div>
      </div>
      <BettieChargeBottomSheet
        isOpen={isBettieChargeBottomSheetOpen}
        onClose={() => setIsBettieChargeBottomSheetOpen(false)}
      />
    </div>
  );
};

export default AutoTopupEventPage;

const ProgressBar = ({ currentStep }: { currentStep: number }) => {
  return (
    <div
      className="flex w-full items-center gap-[2px] rounded-full bg-pure-white"
      aria-label={`자동충전 이벤트 진행도 ${currentStep} / ${MAX_STEP}`}
      role="img"
    >
      {Array.from({ length: MAX_STEP }).map((_, index) =>
        (() => {
          const gradientPosition = (index / Math.max(MAX_STEP - 1, 1)) * 100;

          return (
            <div
              key={`step-${index + 1}`}
              className="h-[15px] flex-1 first:rounded-l-full last:rounded-r-full"
              style={
                index < currentStep
                  ? {
                      backgroundImage: ACTIVE_STEP_GRADIENT,
                      backgroundSize: `${MAX_STEP * 100}% 100%`,
                      backgroundPosition: `${gradientPosition}% 50%`,
                      backgroundRepeat: 'no-repeat',
                      boxShadow:
                        '0 1.98px 2.97px 0 rgba(0, 0, 0, 0.10), 0 3.96px 3.96px 0 rgba(255, 255, 255, 0.25) inset'
                    }
                  : { backgroundColor: '#F6F6F6' }
              }
            />
          );
        })()
      )}
    </div>
  );
};
